import { MAX_INPUT_VALUE } from "@inrev/common";
import { type ReactNode, useEffect, useState } from "react";
import {
	type FieldError,
	type FieldPath,
	type FieldValues,
	type UseControllerProps,
	useController,
} from "react-hook-form";
import { FormFieldError } from "../FormFieldError";
import { Input } from "../inputs/Input";

export type FormNumberProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> & {
	compact?: boolean;
	className?: string;
	containerClassName?: string;
	placeholder?: string;
	disabled?: boolean;
	errorMessage?: string;
	min?: number;
	max?: number;
	allowDecimals?: boolean;
	allowNegative?: boolean;
	rightAlign?: boolean;
	prefix?: ReactNode;
	suffix?: ReactNode;
	testId?: string;
};

export const FormNumber = <TFieldValues extends FieldValues, TTransformedValues = TFieldValues>({
	name,
	control,
	defaultValue,
	compact,
	className,
	containerClassName,
	placeholder,
	disabled,
	errorMessage,
	min,
	max = MAX_INPUT_VALUE,
	allowDecimals = false,
	allowNegative = false,
	rightAlign = false,
	prefix,
	suffix,
	testId,
}: FormNumberProps<TFieldValues, TTransformedValues>) => {
	const {
		field: { onChange, onBlur, value, ...field },
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
	});

	const [displayValue, setDisplayValue] = useState<string>(() => {
		return value?.toString() ?? "";
	});

	useEffect(() => {
		const newDisplayValue = value?.toString() ?? "";
		setDisplayValue(newDisplayValue);
	}, [value]);

	const [rangeError, setRangeError] = useState<string | null>(null);
	const rangeFieldError: FieldError | undefined = rangeError
		? { type: "range", message: rangeError }
		: undefined;
	const displayError = rangeFieldError ?? error;
	const hasError = !!displayError;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;

		if (inputValue === "") {
			setDisplayValue("");
			setRangeError(null);
			onChange(undefined);
			return;
		}

		let pattern = "^";
		if (allowNegative) {
			pattern += "-?";
		}
		pattern += "\\d*";
		if (allowDecimals) {
			pattern += "(\\.\\d*)?";
		}
		pattern += "$";

		const regex = new RegExp(pattern);

		if (regex.test(inputValue)) {
			const isIntermediateDecimalState = allowDecimals && inputValue.endsWith(".");
			const isIntermediateNegativeState = allowNegative && inputValue === "-";

			if (!isIntermediateDecimalState && !isIntermediateNegativeState) {
				const numValue = parseFloat(inputValue);
				if (!isNaN(numValue)) {
					if (max !== undefined && numValue > max) {
						if (rangeError) return;
						setRangeError("Too large");
					} else {
						setRangeError(null);
					}
					onChange(numValue);
				}
			}

			setDisplayValue(inputValue);
		}
	};

	const handleFocus = () => {
		setRangeError(null);
	};

	const handleBlur = () => {
		const numValue = parseFloat(displayValue);
		if (!isNaN(numValue)) {
			if (max !== undefined && numValue > max) {
				setRangeError("Too large");
			} else if (min !== undefined && numValue < min) {
				setRangeError("Too small");
			} else {
				setRangeError(null);
			}
		} else if (displayValue === "" || displayValue === "-") {
			setDisplayValue("");
			onChange(undefined);
		}
		onBlur();
	};

	return (
		<div className="relative">
			<Input
				{...field}
				value={displayValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				compact={compact}
				className={className}
				containerClassName={containerClassName}
				placeholder={placeholder}
				disabled={disabled}
				type="text"
				inputMode="decimal"
				error={hasError}
				rightAlign={rightAlign}
				prefix={prefix}
				suffix={suffix}
				testId={testId}
			/>
			{hasError && <FormFieldError error={displayError} errorMessage={errorMessage} />}
		</div>
	);
};
